import { useAtom, useSetAtom } from "jotai";
import type { OptionsObject } from "notistack";
import { useSnackbar } from "notistack";
import { useCallback, useMemo } from "react";
import { ModalStore } from "../stores/modal";
import type { MLDialogParams } from "../stores/snackbar";
import { snackBarStore } from "../stores/snackbar";

export interface MLDialogInterface {
  /**
   * @deprecated use {@link useModal} is more flexible
   */
  showModal: (title: string, message: JSX.Element | string, params?: MLDialogParams) => void;

  /**
   * @deprecated use {@link useModal} is more flexible
   */
  hideModal: () => void;

  /**
   * @deprecated use {@link useSnackbar} is more flexible
   */
  hideSnackbar: (key: string | number | undefined) => void;

  /**
   * @deprecated use {@link useSnackbar} is more flexible
   */
  showSnackbar: (message: string, options?: OptionsObject) => string | number;
}

export function useDialog(): MLDialogInterface {
  const setState = useSetAtom(snackBarStore);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const showModal = useCallback(
    function showModal(title: string, message: JSX.Element | string, params?: MLDialogParams) {
      return setState({ ...params, title, message, open: true });
    },
    [setState]
  );

  const hideModal = useCallback(
    function hideModal() {
      return setState({ open: false });
    },
    [setState]
  );

  const hideSnackbar = useCallback(
    function hideSnackbar(key: string | number | undefined) {
      return closeSnackbar(key);
    },
    [closeSnackbar]
  );

  const showSnackbar = useCallback(
    function showSnackbar(message: String, options?: OptionsObject) {
      return enqueueSnackbar(message, options);
    },
    [enqueueSnackbar]
  );

  return useMemo(
    () => ({ hideModal, showModal, hideSnackbar, showSnackbar }),
    [hideModal, hideSnackbar, showModal, showSnackbar]
  );
}

export function useModal(params?: { children?: JSX.Element; onClose?: () => void }) {
  const [modal, setModal] = useAtom(ModalStore);

  function open() {
    return setModal({ ...modal, ...params, isOpen: true });
  }

  function close() {
    return setModal({ ...modal, ...params, isOpen: false });
  }

  return { open, close };
}
